"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BridalFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "How do I secure my wedding date?",
    answer: "To reserve your date, a signed contract and a non-refundable retainer are required. We recommend booking at least 6-12 months in advance, as peak wedding season dates fill quickly. Please reach out via our contact form to check availability for your specific date."
  }, {
    question: "Do you offer bridal trials?",
    answer: "Absolutely. Trials are essential for perfecting your vision. We suggest scheduling your trial 2-3 months before the wedding. During this session, we will refine your look, discuss skin preparation, and ensure every detail aligns with your personal style."
  }, {
    question: "What products do you use?",
    answer: "Joanne Glow Studio utilizes a curated selection of high-end, long-wear luxury cosmetics. We prioritize products that photograph beautifully in high-definition while maintaining a breathable, organic feel on the skin. We are happy to accommodate specific sensitivities."
  }, {
    question: "Can you accommodate large bridal parties?",
    answer: "Yes, we are fully equipped to handle large parties. For groups exceeding four people, we bring in a trusted team of associate artists who share our aesthetic philosophy, ensuring every member of your party receives the same level of meticulous care."
  }, {
    question: "Do you travel to the venue?",
    answer: "We provide on-site services for all bridal bookings. Whether you are getting ready at a hotel, a private estate, or a venue, we bring the full studio experience to you, allowing you to relax and enjoy your morning without the stress of travel."
  }, {
    question: "What is your cancellation policy?",
    answer: "We understand that plans can change. Cancellations made more than 30 days prior to the event date will forfeit the retainer. Cancellations within 30 days of the event are subject to the full balance of the booked services. Please refer to your contract for full details."
  }];
  return <section data-section-id="2378" id="bridal-faq" className="bg-background text-foreground py-24 md:py-32 lg:py-40">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12">
          {}
          <div ref={leftRef} className={`lg:col-span-4 mb-16 lg:mb-0 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <p className="font-default text-xs uppercase tracking-widest text-muted-foreground mb-8">
                — Essential Details
              </p>
              <h2 className="font-serif font-default text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-foreground mb-8">
                Prepar<span className="italic font-normal">ations.</span>
              </h2>
              <p className="font-default text-base md:text-[17px] leading-relaxed text-muted-foreground mb-10 max-w-md">
                Everything you need to know to ensure your beauty experience with 
                Joanne Glow Studio is seamless, luxurious, and perfectly tailored to you.
              </p>
              <p className="font-default text-base text-foreground">
                Still have questions?{" "}
                <Link to="/GetInTouchPage" className="relative inline-block text-foreground group">
                  Contact our studio
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-100 transition-transform duration-500 ease-out group-hover:scale-x-0 group-hover:origin-right" />
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-right scale-x-0 transition-transform duration-500 ease-out delay-150 group-hover:scale-x-100 group-hover:origin-left" />
                </Link>
              </p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 lg:col-start-6 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border/60 last:border-b-0 group">
                  <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden font-serif font-default text-xl md:text-[22px] font-normal text-foreground leading-snug text-left items-center">
                    <span className="flex-1 pr-6">
                      <span className="italic">{faq.question.split(" ")[0]}</span>{" "}
                      {faq.question.split(" ").slice(1).join(" ")}
                    </span>
                    <span className="relative flex items-center justify-center size-6 shrink-0 transition-transform duration-500 ease-out group-data-[state=open]:rotate-[135deg]">
                      <Plus className="size-5 text-foreground" strokeWidth={1} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-0">
                    <p className="font-default text-base md:text-[17px] leading-[1.7] text-muted-foreground max-w-2xl">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}